import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  disconnectCommunication,
  getCommunicationConnectionStatus,
  startCommunicationOAuth,
  type CommunicationProvider,
} from "@/services/communicationOAuthService";

export function useCommunicationConnection(provider: CommunicationProvider, userId: string | undefined) {
  const queryClient = useQueryClient();
  const statusKey = [`${provider}-status`, userId] as const;

  const statusQuery = useQuery({
    queryKey: statusKey,
    queryFn: () => getCommunicationConnectionStatus(provider),
    enabled: !!userId,
    staleTime: 60_000,
  });

  const connectMutation = useMutation({
    mutationFn: () => startCommunicationOAuth(provider),
  });

  const disconnectMutation = useMutation({
    mutationFn: () => disconnectCommunication(provider),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: statusKey });
    },
  });

  return {
    connected: statusQuery.data?.connected ?? false,
    connectionLabel: statusQuery.data?.email || statusQuery.data?.name,
    isLoadingStatus: statusQuery.isLoading,
    refetchStatus: statusQuery.refetch,
    connect: connectMutation.mutateAsync,
    disconnect: disconnectMutation.mutateAsync,
    isConnecting: connectMutation.isPending,
    isDisconnecting: disconnectMutation.isPending,
  };
}
